/**
 * Vinyl-toy sprite rig. Painters author every creature on a 32×32 design grid with shaded
 * primitives (ellipsoids, capsules, slabs, polygons) lit by the world's key light (upper-left,
 * toward the viewer), a backlight rim, one specular, and contact occlusion where a front volume
 * meets the one behind it.
 *
 * The rig renders at any integer scale `S`: primitives are evaluated in continuous design space
 * per output pixel, so a 64×64 (S=2) or 96×96 (S=3) frame has genuinely smooth volumes instead of
 * doubled pixels. At S≥2 each material ramp is interpolated to twice the tones and the silhouette
 * gets a selective (colour-aware) outline — the modern hi-bit pixel read.
 */
export type Rgb = readonly [number, number, number];
export type Ramp = readonly Rgb[];

function h(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Material ramps, light → dark. Hue-shifted: shadows lean violet/cool, lights lean warm. */
export const R = {
  cream: [h("#fff7e2"), h("#f9e7c6"), h("#efd29e"), h("#dab380"), h("#b98f64"), h("#8c6852")],
  orange: [h("#ffdca2"), h("#f7ab5f"), h("#dc783c"), h("#a8522f"), h("#6c3228")],
  brown: [h("#f3d2a6"), h("#d8a674"), h("#b27f54"), h("#87583c"), h("#56362c")],
  bark: [h("#b47e52"), h("#7f5234"), h("#4c2e26")],
  red: [h("#ff8f7c"), h("#dc4852"), h("#8c2544")],
  rose: [h("#ffc6cb"), h("#f27b91"), h("#c34968"), h("#7c2a4c")],
  green: [h("#cdf284"), h("#84d85c"), h("#4fad50"), h("#2f7e46"), h("#1d5038")],
  moss: [h("#a2de6c"), h("#5fae50"), h("#367a46"), h("#1f4a38")],
  blue: [h("#e8fbff"), h("#82e2f3"), h("#40b1e1"), h("#2a72c0"), h("#1e3f88")],
  navy: [h("#62bae9"), h("#3579c3"), h("#244b8e"), h("#182b5c")],
  ice: [h("#ffffff"), h("#d4f5ff"), h("#90d9f1"), h("#4fa1cd")],
  gold: [h("#fff2a4"), h("#ffc83e"), h("#d98a26"), h("#8e5022")],
  stone: [h("#f1ede6"), h("#d0c7ba"), h("#a99d92"), h("#7b7178"), h("#4f4959")],
  slate: [h("#babfdb"), h("#5b6082"), h("#2b2d46")],
  violet: [h("#e4d2ff"), h("#aa7af1"), h("#6b45b9"), h("#3b297a")],
  paper: [h("#fffdf5"), h("#f5ebd3"), h("#ddcba6"), h("#b9a17e")],
  ink: [h("#4b4762"), h("#1d1932")],
  // Frontier materials.
  ember: [h("#fff6b0"), h("#ffd060"), h("#ff9a3c"), h("#e8582c"), h("#9c2a28")],
  lava: [h("#ffe28a"), h("#ff8a3c"), h("#d8402c"), h("#7a1f2c")],
  ash: [h("#a8a3b8"), h("#77728e"), h("#4f4b66"), h("#2f2c44")],
  crystal: [h("#f6ecff"), h("#dcc2ff"), h("#ae92f2"), h("#7d66d2"), h("#4c3d99")],
  aqua: [h("#e6fffb"), h("#a4f3ea"), h("#5fd6cf"), h("#2f99a6"), h("#1d5d76")],
  sand: [h("#fff1cc"), h("#f2d596"), h("#d9ae6a"), h("#b0824a"), h("#7a5838")],
  bog: [h("#c6d68a"), h("#8ca862"), h("#5f7a48"), h("#3a5036"), h("#243426")],
  plum: [h("#f6c2e0"), h("#d27ab4"), h("#9a4686"), h("#5e2860")],
  steel: [h("#eef2f8"), h("#c2cad8"), h("#8e98ae"), h("#5f6780"), h("#3a3f56")],
  // Undernet materials.
  neon: [h("#ffd6fb"), h("#ff7ae6"), h("#e03ccf"), h("#8f1d93"), h("#4d0f5c")],
  lime: [h("#f4ffc2"), h("#c8ff5a"), h("#82e03c"), h("#3f9a2e"), h("#1f5a24")],
  coral: [h("#ffe0d2"), h("#ffa488"), h("#f2665c"), h("#b53a4e"), h("#6e2340")],
  pearl: [h("#ffffff"), h("#f1ecff"), h("#d9cff2"), h("#b2a6d6"), h("#7e75a8")],
  night: [h("#8ea2ff"), h("#5462d8"), h("#343aa0"), h("#1f2064"), h("#12123a")],
  moon: [h("#fffbe0"), h("#fff0a0"), h("#f2d25a"), h("#c89a2c"), h("#7a5a1c")],
  cloud: [h("#ffffff"), h("#eef6ff"), h("#d4e4f8"), h("#a8bedc"), h("#7890b8")],
  chrome: [h("#e6ecf6"), h("#9aa6bc"), h("#5c667e"), h("#343a50"), h("#1c2032")],
  // Sidechain materials.
  koi: [h("#fff4e8"), h("#ffc48a"), h("#ff8a3c"), h("#d8502c"), h("#8a2a24")],
  crow: [h("#9a96b8"), h("#625e82"), h("#3e3a5c"), h("#26223e"), h("#141226")],
  wisp: [h("#f6f2ff"), h("#dcd4f6"), h("#b4a8e0"), h("#8a7cbc"), h("#5c528c")],
  jade: [h("#e0fff2"), h("#94eec8"), h("#50c49c"), h("#2c8c70"), h("#1a5a4c")],
  sandstone: [h("#fff6dc"), h("#f2deae"), h("#d6ba80"), h("#aa8c58"), h("#7a603c")],
  wrap: [h("#fffaf0"), h("#f0e6d0"), h("#d8c8a8"), h("#b09c7c"), h("#7c6a54")],
  bronze: [h("#ffe4a8"), h("#e6ae64"), h("#b67c3c"), h("#7e5028"), h("#4c2e1a")],
  pink: [h("#fff0f6"), h("#ffc2d8"), h("#f28bb0"), h("#c75f8c"), h("#8a3a66")],
  white: [h("#ffffff")],
} as const satisfies Record<string, Ramp>;

const INK: Rgb = h("#1a1430");
const SPEC: Rgb = h("#ffffff");

// Key light direction (unit): from the upper-left, toward the viewer.
const LX = -0.55;
const LY = -0.62;
const LZ = 0.56;

const RIM_D2 = 0.78;
const RIM_FACE = -0.38;

export interface ShadeOpts {
  /** Fixed tone index (skips lighting). */
  flat?: number;
  /** Tone offset after lighting (+ darker, − lighter). */
  shade?: number;
  /** Backlight rim along the far silhouette edge (default on for volumes ≥ 3px). */
  rim?: boolean;
  /** Specular toward the key light (default on for volumes ≥ 4px). */
  spec?: boolean;
  /** Darken what this volume overlaps along its lower/right edge (default on). */
  occ?: boolean;
  /** 0 = flat disc lighting, 1 = full sphere. */
  dome?: number;
}

function toneFor(lam: number, n: number): number {
  const t = Math.floor(((0.9 - lam) / 1.42) * n);
  return t < 0 ? 0 : t >= n ? n - 1 : t;
}

const expanded = new WeakMap<Ramp, Ramp>();
/** Insert a midpoint between every pair of tones (n → 2n − 1). */
function expand(r: Ramp): Ramp {
  let hit = expanded.get(r);
  if (hit) return hit;
  const out: Rgb[] = [];
  for (let i = 0; i < r.length; i++) {
    out.push(r[i]!);
    if (i + 1 < r.length) {
      const a = r[i]!, b = r[i + 1]!;
      out.push([Math.round((a[0] + b[0]) / 2), Math.round((a[1] + b[1]) / 2), Math.round((a[2] + b[2]) / 2)]);
    }
  }
  hit = out;
  expanded.set(r, hit);
  return hit;
}

export class Toy {
  readonly w = 32;
  readonly h = 32;
  readonly S: number;
  /** Output size. */
  readonly W: number;
  /** Tone expansion: internal tones per design tone. */
  private readonly k: number;
  private ramps: (Ramp | null)[];
  private tones: Int8Array;
  private specs: Uint8Array;

  constructor(S = 1) {
    this.S = Math.max(1, Math.floor(S));
    this.W = 32 * this.S;
    this.k = this.S > 1 ? 2 : 1;
    const n = this.W * this.W;
    this.ramps = new Array<Ramp | null>(n).fill(null);
    this.tones = new Int8Array(n);
    this.specs = new Uint8Array(n);
  }

  private len(ramp: Ramp): number {
    return this.k > 1 ? ramp.length * 2 - 1 : ramp.length;
  }

  /** Continuous design coordinate of a fine pixel (pixel-centre convention). */
  private c(X: number): number {
    return (X + 0.5) / this.S - 0.5;
  }

  private hasF(X: number, Y: number): boolean {
    return X >= 0 && Y >= 0 && X < this.W && Y < this.W && this.ramps[Y * this.W + X] !== null;
  }

  private setF(X: number, Y: number, ramp: Ramp, tone: number): void {
    if (X < 0 || Y < 0 || X >= this.W || Y >= this.W) return;
    const i = Y * this.W + X;
    const n = this.len(ramp);
    this.ramps[i] = ramp;
    this.tones[i] = tone < 0 ? 0 : tone >= n ? n - 1 : tone;
    this.specs[i] = 0;
  }

  private darkF(X: number, Y: number, by: number): void {
    if (!this.hasF(X, Y)) return;
    const i = Y * this.W + X;
    this.tones[i] = Math.min(this.len(this.ramps[i]!) - 1, this.tones[i]! + by);
    this.specs[i] = 0;
  }

  has(x: number, y: number): boolean {
    const xi = x | 0, yi = y | 0;
    if (xi < 0 || yi < 0 || xi >= 32 || yi >= 32) return false;
    return this.hasF(xi * this.S + (this.S >> 1), yi * this.S + (this.S >> 1));
  }

  /** Paint one design pixel (an S×S block) of `ramp` at `tone`. */
  px(x: number, y: number, ramp: Ramp, tone = 0): void {
    const xi = x | 0, yi = y | 0;
    if (xi < 0 || yi < 0 || xi >= 32 || yi >= 32) return;
    for (let dy = 0; dy < this.S; dy++) for (let dx = 0; dx < this.S; dx++) this.setF(xi * this.S + dx, yi * this.S + dy, ramp, tone * this.k);
  }

  erase(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= 32 || y >= 32) return;
    for (let dy = 0; dy < this.S; dy++) for (let dx = 0; dx < this.S; dx++) this.ramps[(y * this.S + dy) * this.W + x * this.S + dx] = null;
  }

  darken(x: number, y: number, by = 1): void {
    for (let dy = 0; dy < this.S; dy++) for (let dx = 0; dx < this.S; dx++) this.darkF((x | 0) * this.S + dx, (y | 0) * this.S + dy, by * this.k);
  }

  lighten(x: number, y: number, by = 1): void {
    for (let dy = 0; dy < this.S; dy++) {
      for (let dx = 0; dx < this.S; dx++) {
        const X = (x | 0) * this.S + dx, Y = (y | 0) * this.S + dy;
        if (!this.hasF(X, Y)) continue;
        const i = Y * this.W + X;
        this.tones[i] = Math.max(0, this.tones[i]! - by * this.k);
      }
    }
  }

  rect(x: number, y: number, w: number, h: number, ramp: Ramp, tone = 0): void {
    for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) this.px(x + xx, y + yy, ramp, tone);
  }

  hline(x: number, y: number, w: number, ramp: Ramp, tone = 0): void {
    this.rect(x, y, w, 1, ramp, tone);
  }

  vline(x: number, y: number, h: number, ramp: Ramp, tone = 0): void {
    this.rect(x, y, 1, h, ramp, tone);
  }

  line(x0: number, y0: number, x1: number, y1: number, ramp: Ramp, tone = 0): void {
    if (this.S === 1) {
      let x = x0 | 0, y = y0 | 0;
      const ex = x1 | 0, ey = y1 | 0;
      const dx = Math.abs(ex - x), dy = -Math.abs(ey - y);
      const sx = x < ex ? 1 : -1, sy = y < ey ? 1 : -1;
      let err = dx + dy;
      for (;;) {
        this.px(x, y, ramp, tone);
        if (x === ex && y === ey) break;
        const e2 = 2 * err;
        if (e2 >= dy) { err += dy; x += sx; }
        if (e2 <= dx) { err += dx; y += sy; }
      }
      return;
    }
    // Smooth one-design-pixel stroke.
    const ax = x0 | 0, ay = y0 | 0, bx = x1 | 0, by = y1 | 0;
    const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy || 1;
    const X0 = Math.floor((Math.min(ax, bx) - 1) * this.S), X1 = Math.ceil((Math.max(ax, bx) + 2) * this.S);
    const Y0 = Math.floor((Math.min(ay, by) - 1) * this.S), Y1 = Math.ceil((Math.max(ay, by) + 2) * this.S);
    for (let Y = Y0; Y < Y1; Y++) {
      for (let X = X0; X < X1; X++) {
        const xc = this.c(X), yc = this.c(Y);
        const u = Math.max(0, Math.min(1, ((xc - ax) * dx + (yc - ay) * dy) / len2));
        const d = Math.hypot(xc - (ax + dx * u), yc - (ay + dy * u));
        if (d <= 0.56) this.setF(X, Y, ramp, tone * this.k);
      }
    }
  }

  /** Darken already-painted pixels in a band just outside a volume's lower/right edge. */
  private occlude(cx: number, cy: number, rx: number, ry: number): void {
    const X0 = Math.floor((cx - rx - 2.5) * this.S), X1 = Math.ceil((cx + rx + 2.5) * this.S);
    const Y0 = Math.floor((cy - ry - 2.5) * this.S), Y1 = Math.ceil((cy + ry + 2.5) * this.S);
    for (let Y = Y0; Y <= Y1; Y++) {
      for (let X = X0; X <= X1; X++) {
        const x = this.c(X), y = this.c(Y);
        const nx = (x - cx) / rx, ny = (y - cy) / ry;
        if (nx * nx + ny * ny <= 1) continue;
        const ox = (Math.abs(x - cx) - 1.1) / rx, oy = (Math.abs(y - cy) - 1.1) / ry;
        if (Math.max(0, ox) ** 2 + Math.max(0, oy) ** 2 > 1) continue;
        if (ny > 0.15 || nx > 0.6) this.darkF(X, Y, this.k);
      }
    }
  }

  private markSpec(sx: number, sy: number, ramp: Ramp): void {
    // Specular: a small bright point (one pixel at S≤2, a tiny cross at S≥3).
    const X = Math.round((sx + 0.5) * this.S - 0.5), Y = Math.round((sy + 0.5) * this.S - 0.5);
    const pts: [number, number][] = this.S >= 3 ? [[0, 0], [1, 0], [0, 1], [-1, 0], [0, -1]] : [[0, 0]];
    for (const [dx, dy] of pts) {
      const i = (Y + dy) * this.W + (X + dx);
      if (this.hasF(X + dx, Y + dy) && this.ramps[i] === ramp) this.specs[i] = 1;
    }
  }

  /** Shaded ellipsoid volume. */
  ell(cx: number, cy: number, rx: number, ry: number, ramp: Ramp, o: ShadeOpts = {}): void {
    if (o.occ !== false) this.occlude(cx, cy, rx, ry);
    const n = this.len(ramp);
    const k = this.k;
    const dome = o.dome ?? 1;
    const rim = o.rim !== false && rx >= 3 && ry >= 3 && o.flat === undefined;
    const X0 = Math.floor((cx - rx - 0.5) * this.S), X1 = Math.ceil((cx + rx + 1.5) * this.S);
    const Y0 = Math.floor((cy - ry - 0.5) * this.S), Y1 = Math.ceil((cy + ry + 1.5) * this.S);
    for (let Y = Y0; Y <= Y1; Y++) {
      for (let X = X0; X <= X1; X++) {
        const x = this.c(X), y = this.c(Y);
        const nx = (x - cx) / rx, ny = (y - cy) / ry;
        const d2 = nx * nx + ny * ny;
        if (d2 > 1) continue;
        let t: number;
        if (o.flat !== undefined) t = o.flat * k;
        else {
          const z = Math.sqrt(Math.max(0, 1 - d2 * dome));
          t = toneFor(nx * LX + ny * LY + z * LZ, n) + (o.shade ?? 0) * k;
          if (ny > 0.62) t = Math.min(n - 1, t + k);
          if (rim && d2 > RIM_D2 && nx * LX + ny * LY < RIM_FACE) t = Math.min(Math.max(0, t - 2 * k), k);
        }
        this.setF(X, Y, ramp, t);
      }
    }
    if (o.spec !== false && o.flat === undefined && rx >= 4 && ry >= 4) {
      this.markSpec(Math.round(cx + LX * 0.45 * rx), Math.round(cy + LY * 0.45 * ry), ramp);
    }
  }

  /** Shaded rounded slab (bevelled prop face). */
  slab(cx: number, cy: number, rx: number, ry: number, kr: number, ramp: Ramp, o: ShadeOpts = {}): void {
    const kk = Math.max(0.75, Math.min(kr, Math.min(rx, ry)));
    const ix = rx - kk, iy = ry - kk;
    const bevelU = (x: number) => (Math.sign(x - cx) * Math.max(0, Math.abs(x - cx) - ix)) / kk;
    const bevelV = (y: number) => (Math.sign(y - cy) * Math.max(0, Math.abs(y - cy) - iy)) / kk;
    const X0 = Math.floor((cx - rx - 2.5) * this.S), X1 = Math.ceil((cx + rx + 2.5) * this.S);
    const Y0 = Math.floor((cy - ry - 2.5) * this.S), Y1 = Math.ceil((cy + ry + 2.5) * this.S);
    const k = this.k;
    if (o.occ !== false) {
      const gk = kk + 1.1, gx = rx + 1.1 - gk, gy = ry + 1.1 - gk;
      for (let Y = Y0; Y <= Y1; Y++) {
        for (let X = X0; X <= X1; X++) {
          const x = this.c(X), y = this.c(Y);
          const u = bevelU(x), v = bevelV(y);
          if (u * u + v * v <= 1) continue;
          const gu = Math.max(0, Math.abs(x - cx) - gx) / gk, gv = Math.max(0, Math.abs(y - cy) - gy) / gk;
          if (gu * gu + gv * gv > 1) continue;
          if (y > cy || x > cx) this.darkF(X, Y, k);
        }
      }
    }
    const n = this.len(ramp);
    const dome = o.dome ?? 1;
    const rim = o.rim !== false && kk >= 1.4 && o.flat === undefined;
    for (let Y = Y0; Y <= Y1; Y++) {
      for (let X = X0; X <= X1; X++) {
        const x = this.c(X), y = this.c(Y);
        if (Math.abs(x - cx) > rx + 0.5 || Math.abs(y - cy) > ry + 0.5) continue;
        const u = bevelU(x), v = bevelV(y);
        const d2 = u * u + v * v;
        if (d2 > 1) continue;
        let t: number;
        if (o.flat !== undefined) t = o.flat * k;
        else {
          const z = Math.sqrt(Math.max(0, 1 - d2 * dome));
          t = toneFor(u * LX + v * LY + z * LZ, n) + (o.shade ?? 0) * k;
          if (y - cy > ry * 0.62) t = Math.min(n - 1, t + k);
          if (rim && d2 > RIM_D2 - 0.09 && u * LX + v * LY < RIM_FACE) t = Math.min(Math.max(0, t - 2 * k), k);
        }
        this.setF(X, Y, ramp, t);
      }
    }
    if (o.spec !== false && o.flat === undefined && rx >= 3 && ry >= 3) {
      this.markSpec(Math.round(cx - (ix + kk * 0.5) * 0.62), Math.round(cy - (iy + kk * 0.5) * 0.7), ramp);
    }
  }

  /** Shaded capsule (limb / neck / tail segment) of radius r between two points. */
  cap(x0: number, y0: number, x1: number, y1: number, r: number, ramp: Ramp, o: ShadeOpts = {}): void {
    const n = this.len(ramp);
    const k = this.k;
    const dx = x1 - x0, dy = y1 - y0;
    const len2 = dx * dx + dy * dy || 1;
    const X0 = Math.floor((Math.min(x0, x1) - r - 1.5) * this.S), X1 = Math.ceil((Math.max(x0, x1) + r + 1.5) * this.S);
    const Y0 = Math.floor((Math.min(y0, y1) - r - 1.5) * this.S), Y1 = Math.ceil((Math.max(y0, y1) + r + 1.5) * this.S);
    const rim = o.rim !== false && r >= 2.5 && o.flat === undefined;
    if (o.occ !== false) {
      for (let Y = Y0; Y <= Y1; Y++) {
        for (let X = X0; X <= X1; X++) {
          const x = this.c(X), y = this.c(Y);
          const u = Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / len2));
          const px = x - (x0 + dx * u), py = y - (y0 + dy * u);
          const d = Math.hypot(px, py);
          if (d > r && d <= r + 1.1 && (py > 0.3 || px > 0.6)) this.darkF(X, Y, k);
        }
      }
    }
    for (let Y = Y0; Y <= Y1; Y++) {
      for (let X = X0; X <= X1; X++) {
        const x = this.c(X), y = this.c(Y);
        const u = Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / len2));
        const px = x - (x0 + dx * u), py = y - (y0 + dy * u);
        const d2 = (px * px + py * py) / (r * r);
        if (d2 > 1) continue;
        let t: number;
        if (o.flat !== undefined) t = o.flat * k;
        else {
          const nx = px / r, ny = py / r;
          const z = Math.sqrt(Math.max(0, 1 - d2));
          t = toneFor(nx * LX + ny * LY + z * LZ, n) + (o.shade ?? 0) * k;
          if (rim && d2 > RIM_D2 - 0.04 && nx * LX + ny * LY < RIM_FACE) t = Math.min(Math.max(0, t - 2 * k), k);
        }
        this.setF(X, Y, ramp, t);
      }
    }
    if (o.spec !== false && o.flat === undefined && r >= 3) {
      this.markSpec(Math.round(x0 + dx * 0.35 + LX * 0.4 * r), Math.round(y0 + dy * 0.35 + LY * 0.4 * r), ramp);
    }
  }

  /** Flat or planar-shaded polygon (ears, wings, fins, flames). */
  poly(pts: readonly (readonly [number, number])[], ramp: Ramp, o: { tone?: number; grad?: boolean } = {}): void {
    let minX = 32, maxX = -1, minY = 32, maxY = -1;
    for (const [x, y] of pts) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    const hw = Math.max(1, (maxX - minX) / 2), hh = Math.max(1, (maxY - minY) / 2);
    const base = o.tone ?? 1;
    const S = this.S, k = this.k;
    for (let Y = Math.floor(minY * S); Y <= Math.ceil(maxY * S); Y++) {
      const yc = (Y + 0.5) / S;
      const xs: number[] = [];
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i]!;
        const [bx, by] = pts[(i + 1) % pts.length]!;
        if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) xs.push(ax + ((yc - ay) * (bx - ax)) / (by - ay));
      }
      xs.sort((a, b) => a - b);
      for (let q = 0; q + 1 < xs.length; q += 2) {
        for (let X = Math.round(xs[q]! * S); X < Math.round(xs[q + 1]! * S); X++) {
          const xd = (X + 0.5) / S - 0.5, yd = yc - 0.5;
          const t = o.grad ? base * k + Math.round(k * (((xd - cx) / hw) * 0.55 + ((yd - cy) / hh) * 0.62)) : base * k;
          this.setF(X, Y, ramp, t);
        }
      }
    }
  }

  /** 1px ellipse ring (rings, crowns, orbits). */
  ring(cx: number, cy: number, rx: number, ry: number, ramp: Ramp, tone = 0): void {
    if (this.S === 1) {
      const steps = Math.max(24, Math.round((rx + ry) * 4));
      for (let i = 0; i < steps; i++) {
        const a = (i / steps) * Math.PI * 2;
        this.px(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * ry), ramp, tone);
      }
      return;
    }
    const X0 = Math.floor((cx - rx - 1.5) * this.S), X1 = Math.ceil((cx + rx + 1.5) * this.S);
    const Y0 = Math.floor((cy - ry - 1.5) * this.S), Y1 = Math.ceil((cy + ry + 1.5) * this.S);
    const rr = (rx + ry) / 2;
    for (let Y = Y0; Y <= Y1; Y++) {
      for (let X = X0; X <= X1; X++) {
        const nx = (this.c(X) - cx) / rx, ny = (this.c(Y) - cy) / ry;
        const d = Math.abs(Math.sqrt(nx * nx + ny * ny) - 1) * rr;
        if (d <= 0.5) this.setF(X, Y, ramp, tone * this.k);
      }
    }
  }

  /** Mirror the painted left half onto the right (symmetric fronts / backs). */
  mirror(axis = 15.5): void {
    const W = this.W, S = this.S;
    for (let Y = 0; Y < W; Y++) {
      for (let X = 0; X < W; X++) {
        if (this.c(X) > axis) continue;
        const MX = Math.round((2 * axis + 1) * S - 1 - X);
        if (MX < 0 || MX >= W) continue;
        const i = Y * W + X, j = Y * W + MX;
        this.ramps[j] = this.ramps[i]!;
        this.tones[j] = this.tones[i]!;
        this.specs[j] = 0;
      }
    }
  }

  /** Resolve to RGBA with the outline around the silhouette. */
  toImage(): ImageData {
    const W = this.W, S = this.S, k = this.k;
    // Sharp vinyl: a key-side kiss on the upper-left silhouette, a darker foot contact.
    for (let Y = 0; Y < W; Y++) {
      for (let X = 0; X < W; X++) {
        if (!this.hasF(X, Y)) continue;
        const i = Y * W + X;
        if (this.specs[i]) continue;
        const x = X / S, y = Y / S;
        if ((!this.hasF(X - 1, Y) || !this.hasF(X, Y - 1)) && x + y < 28 && this.tones[i]! >= k) {
          this.tones[i] = this.tones[i]! - k;
          if (!this.hasF(X - 1, Y) && !this.hasF(X, Y - 1) && this.tones[i]! >= k) this.tones[i] = this.tones[i]! - k;
        }
        if (y >= 25 && !this.hasF(X, Y + 1)) this.darkF(X, Y, k);
        if (y >= 28 && !this.hasF(X, Y + 1)) this.darkF(X, Y, k);
      }
    }
    const img = new ImageData(W, W);
    const d = img.data;
    for (let i = 0; i < W * W; i++) {
      const ramp = this.ramps[i];
      if (!ramp) continue;
      const r = k > 1 ? expand(ramp) : ramp;
      const c = this.specs[i] ? SPEC : r[this.tones[i]!]!;
      const o = i * 4;
      d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
    }
    for (let Y = 0; Y < W; Y++) {
      for (let X = 0; X < W; X++) {
        const i = Y * W + X;
        if (this.ramps[i]) continue;
        let nb = -1;
        for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1]] as const) {
          if (this.hasF(X + dx, Y + dy)) { nb = (Y + dy) * W + X + dx; break; }
        }
        if (nb < 0) continue;
        const o = i * 4;
        if (S === 1) {
          d[o] = INK[0]; d[o + 1] = INK[1]; d[o + 2] = INK[2];
        } else {
          // Selective outline: the neighbour's darkest tone pulled toward ink.
          const ramp = this.ramps[nb]!;
          const dk = ramp[ramp.length - 1]!;
          d[o] = Math.round(dk[0] * 0.35 + INK[0] * 0.65);
          d[o + 1] = Math.round(dk[1] * 0.35 + INK[1] * 0.65);
          d[o + 2] = Math.round(dk[2] * 0.35 + INK[2] * 0.65);
        }
        d[o + 3] = 255;
      }
    }
    return img;
  }
}
